import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw } from 'lucide-react';

import { api, ApiError } from '@/lib/api';
import { useResource } from './useSettings';

/**
 * Print queue (wave 3 #18) — the queue behind every physical print.
 *
 * Named "Print Jobs" until 2026-09-20; the owner calls it the queue, and the
 * work here is clearing it. The route followed the name to `/settings/print-queue`
 * (the old one redirects); the `/print-jobs` API and its locale keys did not.
 *
 * A checkout or kitchen order creates a job; the server pushes it to the print
 * bridge; the bridge answers with the outcome. This screen is where a FAILED
 * job (paper out, printer unplugged) is seen and retried — the automatic sweep
 * gives up after 5 attempts precisely so that a human decides here, after
 * fixing the printer, whether the paper is still wanted.
 */

const STATUSES = ['QUEUED', 'SENT', 'PRINTED', 'FAILED'] as const;
type JobStatus = (typeof STATUSES)[number];

interface PrintJobRow {
  id: string;
  kind: string;
  status: JobStatus;
  copies: number;
  attempts: number;
  lastError: string | null;
  printerName: string;
  billId: string | null;
  invoiceNumber: string | null;
  createdAt: string;
  sentAt: string | null;
  printedAt: string | null;
}

export function PrintQueuePage() {
  const { t, i18n } = useTranslation();
  const [status, setStatus] = useState<JobStatus | null>(null);
  const [retryingId, setRetryingId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const path = status ? `/print-jobs?status=${status}` : '/print-jobs';
  const { data: jobs, loading, error, reload } = useResource<PrintJobRow[]>(path);

  const dateTime = (iso: string) =>
    new Intl.DateTimeFormat(i18n.language, {
      dateStyle: 'short',
      timeStyle: 'medium',
      numberingSystem: 'latn',
    }).format(new Date(iso));

  async function handleRetry(jobId: string): Promise<void> {
    setRetryingId(jobId);
    setActionError(null);
    try {
      const { outcome } = await api.post<{ outcome: string }>(`/print-jobs/${jobId}/retry`);
      if (outcome !== 'sent') {
        setActionError(t(`settings.printJobs.outcome.${outcome}`, outcome));
      }
    } catch (caught) {
      setActionError(
        caught instanceof ApiError ? caught.message : t('settings.printJobs.retryFailed'),
      );
    } finally {
      setRetryingId(null);
      await reload();
    }
  }

  const chipClass = (active: boolean) =>
    [
      'rounded-full border px-3 py-1 text-sm transition-colors',
      active
        ? 'border-primary bg-primary text-primary-fg'
        : 'border-subtle text-content-secondary hover:bg-hover',
    ].join(' ');

  return (
    <div className="p-6">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold text-content">{t('settings.nav.printQueue')}</h1>
        <button
          type="button"
          onClick={() => void reload()}
          title={t('settings.printJobs.refresh')}
          aria-label={t('settings.printJobs.refresh')}
          className="grid h-8 w-8 place-items-center rounded-control text-content-secondary hover:bg-hover hover:text-content"
        >
          <RefreshCw className="h-4 w-4" aria-hidden />
        </button>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <button
          type="button"
          className={chipClass(status === null)}
          onClick={() => setStatus(null)}
        >
          {t('settings.printJobs.allStatuses')}
        </button>
        {STATUSES.map((candidate) => (
          <button
            key={candidate}
            type="button"
            className={chipClass(status === candidate)}
            onClick={() => setStatus(candidate)}
          >
            {t(`settings.printJobs.status.${candidate}`)}
          </button>
        ))}
      </div>

      {actionError ? (
        <p role="alert" className="mb-3 text-sm text-danger">
          {actionError}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : jobs && jobs.length > 0 ? (
        <div className="overflow-x-auto rounded-card border border-subtle bg-raised">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-subtle text-start">
                <Th label={t('settings.printJobs.columns.created')} />
                <Th label={t('settings.printJobs.columns.kind')} />
                <Th label={t('settings.printJobs.columns.printer')} />
                <Th label={t('settings.printJobs.columns.invoice')} />
                <Th label={t('settings.printJobs.columns.copies')} />
                <Th label={t('settings.printJobs.columns.attempts')} />
                <Th label={t('settings.printJobs.columns.status')} />
                <Th label={t('settings.printJobs.columns.error')} />
                <Th label="" />
              </tr>
            </thead>
            <tbody>
              {jobs.map((job) => (
                <tr key={job.id} className="border-b border-subtle/50">
                  <td className="tabular px-3 py-2 text-content">{dateTime(job.createdAt)}</td>
                  <td className="px-3 py-2 text-content">
                    {t(`settings.printJobs.kind.${job.kind}`, job.kind)}
                  </td>
                  <td className="px-3 py-2 text-content">{job.printerName}</td>
                  <td className="px-3 py-2 text-content">{job.invoiceNumber ?? '—'}</td>
                  <td className="tabular px-3 py-2 text-content">{job.copies}</td>
                  <td className="tabular px-3 py-2 text-content">{job.attempts}</td>
                  <td className="px-3 py-2">
                    <StatusBadge status={job.status} />
                  </td>
                  <td
                    className="max-w-56 truncate px-3 py-2 text-xs text-content-muted"
                    title={job.lastError ?? undefined}
                  >
                    {job.lastError ?? '—'}
                  </td>
                  <td className="px-3 py-2 text-end">
                    {job.status === 'FAILED' || job.status === 'QUEUED' ? (
                      <button
                        type="button"
                        disabled={retryingId === job.id}
                        onClick={() => void handleRetry(job.id)}
                        className="rounded-full border border-subtle px-3 py-1 text-sm text-content hover:bg-hover disabled:opacity-50"
                      >
                        {retryingId === job.id
                          ? t('settings.printJobs.retrying')
                          : t('settings.printJobs.retry')}
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="text-sm text-content-muted">{t('settings.printJobs.empty')}</p>
      )}

      <div className="mt-6 max-w-2xl space-y-2 text-xs text-content-muted">
        {/* Where the paper physically comes from — support calls start here. */}
        <p>{t('settings.printJobs.bridgeNote')}</p>
        {/* Honest about the deferred ESC/POS Arabic shaping work. */}
        <p>{t('settings.printJobs.arabicNote')}</p>
      </div>
    </div>
  );
}

function Th({ label }: { label: string }) {
  return (
    <th
      scope="col"
      className="px-3 py-2 text-start text-xs font-medium uppercase tracking-wide text-content-muted"
    >
      {label}
    </th>
  );
}

function StatusBadge({ status }: { status: JobStatus }) {
  const { t } = useTranslation();
  // Card 11's job badges. (`text-success` sat here before — a class this
  // design system never defined, so PRINTED rendered in the default ink.)
  const tone =
    status === 'PRINTED'
      ? 'border-[rgba(52,211,153,0.4)] text-available'
      : status === 'FAILED'
        ? 'border-[rgba(244,63,94,0.4)] text-danger'
        : status === 'SENT'
          ? 'border-[var(--border-live)] text-primary'
          : 'border-strong text-content-secondary';
  return (
    <span
      className={`inline-block rounded-full border bg-overlay px-2 py-0.5 text-[10px] uppercase tracking-[0.12em] ${tone}`}
    >
      {t(`settings.printJobs.status.${status}`)}
    </span>
  );
}
